﻿import { useCallback, useEffect, useMemo, useState } from "react";
import { AdminButton, AdminErrorBanner, AdminLoadingState, AdminPageLayout } from "@/components/admin";
import MonitoringSummaryCards from "./components/MonitoringSummaryCards";
import MonitoringList from "./components/MonitoringList";
import QuickFilters from "./components/QuickFilters";
import TodaySummaryCard from "./components/TodaySummaryCard";
import MonitoringInfoCard from "./components/MonitoringInfoCard";
import { fetchAdminLifeSignalSummary, fetchAdminLifeSignals } from "../../../lib/api";
import type { AdminLifeSignalItem, AdminLifeSignalSummary } from "@/types.ts";

const LifeSignalPage = () => {
    const [activeFilter, setActiveFilter] = useState("all");
    const [searchQuery, setSearchQuery] = useState("");
    const [summary, setSummary] = useState<AdminLifeSignalSummary | null>(null);
    const [items, setItems] = useState<AdminLifeSignalItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState("");
    const loadPage = useCallback(async () => {
        setLoading(true);
        setErrorMessage("");
        try {
            const [nextItems, nextSummary] = await Promise.all([
                fetchAdminLifeSignals(),
                fetchAdminLifeSignalSummary(),
            ]);
            setSummary(nextSummary);
            setItems(nextItems);
        }
        catch (error) {
            setErrorMessage(error instanceof Error ? error.message : "생활 모니터링 데이터를 불러오지 못했습니다.");
        }
        finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => {
        void loadPage();
    }, [loadPage]);
    const pageSubtitle = useMemo(() => {
        if (loading)
            return "생활 모니터링 데이터를 불러오는 중";
        return `대상 ${items.length}명 · 확인 필요 ${summary?.totalNeedsAttentionCount ?? 0}명`;
    }, [items.length, loading, summary?.totalNeedsAttentionCount]);
    return (<AdminPageLayout
            title="생활 모니터링"
            subtitle={pageSubtitle}
            actions={
              <AdminButton type="button" variant="primary" size="sm" onClick={() => void loadPage()} disabled={loading} icon="ri-refresh-line">
                {loading ? "불러오는 중" : "새로고침"}
              </AdminButton>
            }
          >
          <AdminErrorBanner message={errorMessage}/>

          {loading ? (<AdminLoadingState message="생활 모니터링 데이터를 불러오는 중입니다."/>) : (<>
              <div className="mb-4">
                <MonitoringSummaryCards summary={summary} items={items}/>
              </div>

              <div className="grid grid-cols-1 xl:grid-cols-4 gap-4">
                <div className="xl:col-span-3">
                  <MonitoringList targets={items} totalCount={items.length} searchQuery={searchQuery} onSearchChange={setSearchQuery} activeFilter={activeFilter} onRefresh={() => void loadPage()}/>
                </div>

                <div className="xl:col-span-1 flex flex-col gap-4">
                  <QuickFilters activeFilter={activeFilter} onFilterChange={setActiveFilter}/>

                  <TodaySummaryCard summary={summary} items={items}/>

                  <MonitoringInfoCard />
                </div>
              </div>
            </>)}
    </AdminPageLayout>);
};
export default LifeSignalPage;
